'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/app/providers';
import { Mail, Lock, Anchor, Ban, AlertCircle, ArrowRight, Loader2, Eye, EyeOff } from 'lucide-react';
import { t } from '@/locales';

interface LoginFormCardProps {
  onLoginSuccess?: (user: any) => void;
  onClose?: () => void;
  onOpenRegister?: () => void;
  returnUrl?: string | null;
}

export const LoginFormCard: React.FC<LoginFormCardProps> = ({ onLoginSuccess, onClose, onOpenRegister, returnUrl }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSuspended, setIsSuspended] = useState(false);
  const [loading, setLoading] = useState(false);
  // 2FA TOTP step
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const { login } = useAuth();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password, totpRequired ? totpToken : undefined);
      onLoginSuccess?.(user);

      // Admin: sempre alla dashboard admin, ignorando returnUrl (niente area
      // partner/pubblica per l'admin). L'impersonation usa un role effettivo ≠ ADMIN.
      if (user.role === 'ADMIN') {
        window.location.href = '/admin';
        return;
      }

      // returnUrl può essere URL assoluto (es. https://partner.insulacharter.it/...)
      // se proviene da redirect cross-subdomain. Usa window.location per gestirli.
      if (returnUrl) {
        const decoded = decodeURIComponent(returnUrl);
        // Hard-nav anche per path interni quando ci sono query params:
        // router.push può riusare il client component cachato e non
        // aggiornare useSearchParams() → initialBookingData rimane stale
        // e il MobileBookingFlow/BookingSidebar mobile non si renderizza.
        if (/^https?:\/\//.test(decoded) || decoded.includes('?')) {
          window.location.href = decoded;
        } else {
          router.push(decoded);
        }
        return;
      }

      // Redirect per ruolo — PATH-based su qualsiasi host (prod/staging/locale).
      if (user.role === 'VENDOR' || user.role === 'COMPANY') {
        // /partner instrada per ruolo a /vendor o /excursion.
        window.location.href = '/partner';
        return;
      }
      if (user.role === 'SERVICE_PROVIDER') {
        window.location.href = 'https://insulaservices.it/dashboard';
        return;
      }
      router.push('/');
    } catch (err) {
      const code = (err as any)?.code;
      if (code === 'ACCOUNT_SUSPENDED') {
        setIsSuspended(true);
        setError(err instanceof Error ? err.message : t('admin.accountSuspended'));
      } else if (code === 'TOTP_REQUIRED') {
        // Credenziali OK, serve secondo step 2FA
        setTotpRequired(true);
        setError('');
      } else if (code === 'TOTP_INVALID') {
        setError('Codice 2FA non valido. Verifica orologio device.');
      } else {
        setIsSuspended(false);
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-cid="auth-login-form-card" className="relative">
      {/* Edge refraction: inner top highlight */}
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" aria-hidden />

      {/* Brand row — left-aligned, asymmetric */}
      <div
        className="flex items-center gap-3 mb-10 opacity-0 animate-fade-in"
        style={{ animationDelay: '40ms', animationFillMode: 'forwards' }}
        data-cid="auth-login-brand"
      >
        <div className="relative">
          <div className="bg-slate-950 p-2.5 rounded-2xl ring-1 ring-inset ring-white/5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
            <Anchor className="text-sky-300" size={20} strokeWidth={2} />
          </div>
          <div className="absolute -inset-1 rounded-2xl ring-1 ring-slate-200/60 opacity-60" aria-hidden />
        </div>
        <span className="font-display text-2xl font-bold tracking-tight text-slate-950">
          Insula<span className="font-light text-slate-500">Charter</span>
        </span>
      </div>

      {/* Headline — asymmetric, left-aligned */}
      <div
        className="mb-8 opacity-0 animate-fade-in"
        style={{ animationDelay: '120ms', animationFillMode: 'forwards' }}
        data-cid="auth-login-headline"
      >
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-700 mb-2">
          {t('login.subtitle')}
        </p>
        <h2 className="font-display text-4xl font-black tracking-tight leading-none text-slate-950">
          {t('login.title')}
        </h2>
      </div>

      {/* Error states */}
      {error && isSuspended ? (
        <div
          data-cid="auth-login-error-suspended"
          className="mb-6 rounded-2xl border border-rose-200 bg-rose-50/70 p-4 animate-fade-in"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 p-1.5 bg-rose-100 rounded-lg">
              <Ban size={16} className="text-rose-700" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-rose-900 font-bold text-sm mb-1">
                {t('admin.accountSuspended')}
              </h3>
              <p className="text-rose-700 text-[13px] leading-snug">
                {t('admin.accountSuspendedDesc')}
              </p>
            </div>
          </div>
        </div>
      ) : error ? (
        <div
          data-cid="auth-login-error"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50/70 px-4 py-3 animate-fade-in"
          role="alert"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-rose-700" strokeWidth={2} />
          <p className="text-rose-800 text-sm font-medium leading-snug">{error}</p>
        </div>
      ) : null}

      <form
        onSubmit={handleSubmit}
        className="space-y-5 opacity-0 animate-fade-in"
        style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        data-cid="auth-login-form"
      >
        {/* Email */}
        <div className="flex flex-col gap-2" data-cid="auth-login-field-email">
          <label htmlFor="login-email" className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {t('login.emailLabel')}
          </label>
          <div className="group relative">
            <Mail
              size={16}
              strokeWidth={2}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-900"
            />
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-2xl text-slate-950 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/8 hover:border-slate-300"
              placeholder={t('login.emailPlaceholder')}
            />
          </div>
        </div>

        {/* Password */}
        <div className="flex flex-col gap-2" data-cid="auth-login-field-password">
          <div className="flex items-baseline justify-between">
            <label htmlFor="login-password" className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {t('login.passwordLabel')}
            </label>
            <Link
              href="/forgot-password"
              onClick={onClose}
              className="text-[11px] font-semibold text-slate-600 underline-offset-4 hover:underline hover:text-slate-900 transition-colors"
              data-cid="auth-login-forgot-link"
            >
              {t('login.forgotPassword')}
            </Link>
          </div>
          <div className="group relative">
            <Lock
              size={16}
              strokeWidth={2}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-900"
            />
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full pl-11 pr-12 py-3.5 bg-white border border-slate-200 rounded-2xl text-slate-950 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/8 hover:border-slate-300"
              placeholder={t('login.passwordPlaceholder')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              data-cid="auth-login-toggle-password"
            >
              {showPassword ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
            </button>
          </div>
        </div>

        {/* 2FA TOTP step — appare solo se backend ha risposto TOTP_REQUIRED */}
        {totpRequired && (
          <div data-cid="auth-login-totp-field" className="flex flex-col gap-2 mt-2 p-4 rounded-2xl bg-amber-50 border-2 border-amber-200">
            <label htmlFor="login-totp" className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
              Codice 2FA (6 cifre)
            </label>
            <input
              id="login-totp"
              data-cid="auth-login-totp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              value={totpToken}
              onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="w-full px-4 py-3 rounded-xl border-2 border-amber-300 bg-white text-2xl font-mono font-bold text-center tracking-[0.4em] text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <p className="text-[11px] text-amber-700 leading-snug">
              Apri il tuo autenticatore (Google/Authy/1Password) e inserisci il codice corrente per InsulaCharter.
            </p>
          </div>
        )}

        {/* CTA */}
        <button
          type="submit"
          disabled={loading || (totpRequired && totpToken.length !== 6)}
          data-cid="auth-login-submit"
          className="group relative w-full mt-2 inline-flex items-center justify-center gap-2 bg-slate-950 text-white py-4 rounded-2xl font-bold text-[15px] tracking-tight shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ring-1 ring-inset ring-white/5 transition-all duration-200 hover:bg-slate-800 active:scale-[0.985] active:translate-y-px disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 disabled:active:translate-y-0"
        >
          {loading ? (
            <>
              <Loader2 size={16} strokeWidth={2.2} className="animate-spin" />
              <span>{t('login.submittingButton')}</span>
            </>
          ) : (
            <>
              <span>{t('login.submitButton')}</span>
              <ArrowRight size={16} strokeWidth={2.2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </form>

      {/* Divider + register */}
      <div
        className="mt-8 pt-6 border-t border-slate-200/80 opacity-0 animate-fade-in"
        style={{ animationDelay: '280ms', animationFillMode: 'forwards' }}
        data-cid="auth-login-register-row"
      >
        <p className="text-center text-sm text-slate-600">
          {onOpenRegister ? (
            <button
              type="button"
              onClick={onOpenRegister}
              className="font-semibold text-slate-900 underline-offset-4 hover:underline transition-colors"
              data-cid="auth-login-register-cta"
            >
              {t('login.noAccount')}
            </button>
          ) : (
            <Link
              href="/register"
              onClick={onClose}
              className="font-semibold text-slate-900 underline-offset-4 hover:underline transition-colors"
              data-cid="auth-login-register-cta"
            >
              {t('login.noAccount')}
            </Link>
          )}
        </p>
      </div>
    </div>
  );
};

export default LoginFormCard;
